import React, { useRef, useEffect } from "react";
import { Tooltip } from "antd";
import { CheckOutlined } from "@ant-design/icons";
import { MoreHorizontal } from "lucide-react";
import type { ModelEntry } from "@/lib/types";

interface ChatModelSelectorProps {
  models: ModelEntry[];
  selectedModel: string;
  onSelect: (modelId: string) => void;
  visible: boolean;
  onToggle: () => void;
  onClose?: () => void;
}

const ChatModelSelector: React.FC<ChatModelSelectorProps> = ({
  models,
  selectedModel,
  onSelect,
  visible,
  onToggle,
  onClose,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!visible) return undefined;

    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        if (onClose) onClose();
        else onToggle();
      }
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        if (onClose) onClose();
        else onToggle();
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [visible, onClose, onToggle]);

  return (
    <div className="chat-model-selector" ref={containerRef}>
      <Tooltip title="Select Model" placement="top">
        <button
          type="button"
          onClick={onToggle}
          className={`chat-header-btn ${visible ? "active" : ""}`}
          aria-label="Select Model"
        >
          <MoreHorizontal size={15} />
        </button>
      </Tooltip>
      {visible && (
        <div className="chat-model-dropdown">
          <div className="chat-model-dropdown-header">
            <span>Models</span>
          </div>
          <div className="chat-model-dropdown-list">
            {models.filter((m) => m.is_enabled).map((model) => (
              <button
                key={model.id}
                type="button"
                onClick={() => {
                  onSelect(model.id);
                  if (onClose) onClose();
                  else onToggle();
                }}
                className={`chat-model-option ${selectedModel === model.id ? "selected" : ""}`}
              >
                <span>{model.display_name}</span>
                {selectedModel === model.id && (
                  <CheckOutlined style={{ fontSize: 12, color: "var(--primary-color, #0069a5)" }} />
                )}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default ChatModelSelector;
